import React, { useState } from "react";
import { Alert, Box, Collapse, MenuItem, TextField, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import {
  IPD_COLORS,
  IPD_SHAPE,
  getIpdSelectMenuProps,
  ipdElevatedInputSx,
  ipdLabelSx,
  ipdSemanticSx,
  IpdButton,
  IpdSectionCard,
} from "../../ipd/utils";
import { formatDateTime } from "../constants";
import { saveRiskAssessment } from "../api/emergencyApi";

/**
 * Fall risk, pressure-ulcer risk and pain score (ticket 15).
 *
 * One row per visit, edited in place — the latest recorded values are the ones
 * shown, not a history of every reassessment. Each is optional and independent;
 * a pain score can be recorded before any risk is assessed.
 */

const LEVEL = {
  high: { label: "High", variant: "error" },
  medium: { label: "Medium", variant: "warning" },
  low: { label: "Low", variant: "success" },
};

const LEVEL_OPTIONS = [
  { value: "", label: "Not assessed" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

function LevelChip({ level }) {
  const m = LEVEL[level];
  if (!m) {
    return (
      <Typography component="span" sx={{ fontSize: 13, color: IPD_COLORS.textMuted }}>
        Not assessed
      </Typography>
    );
  }
  return (
    <Box
      component="span"
      sx={{
        ...ipdSemanticSx(m.variant),
        px: 0.9,
        py: 0.2,
        borderRadius: `${IPD_SHAPE.radiusSm}px`,
        fontSize: 11.5,
        whiteSpace: "nowrap",
      }}
    >
      {m.label}
    </Box>
  );
}

/** Pain 0–3 reads calm, 4–6 moderate, 7–10 severe. */
function painVariant(score) {
  if (score == null) return null;
  if (score >= 7) return "error";
  if (score >= 4) return "warning";
  return "success";
}

function Row({ label, children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, py: 0.6 }}>
      <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>{label}</Typography>
      <Box sx={{ display: "flex", alignItems: "center" }}>{children}</Box>
    </Box>
  );
}

export default function RiskAssessmentCard({ visit, onChanged, disabled }) {
  const risk = visit.risk_assessment || null;

  const [editing, setEditing] = useState(false);
  const [fallRisk, setFallRisk] = useState(risk?.fall_risk || "");
  const [pressureUlcerRisk, setPressureUlcerRisk] = useState(risk?.pressure_ulcer_risk || "");
  const [painScore, setPainScore] = useState(
    risk?.pain_score == null ? "" : String(risk.pain_score)
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const openEditor = () => {
    setFallRisk(risk?.fall_risk || "");
    setPressureUlcerRisk(risk?.pressure_ulcer_risk || "");
    setPainScore(risk?.pain_score == null ? "" : String(risk.pain_score));
    setError("");
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await saveRiskAssessment(visit.id, { fallRisk, pressureUlcerRisk, painScore });
      await onChanged();
      setEditing(false);
    } catch (e) {
      setError(e?.message || "Could not save the risk assessment");
    } finally {
      setBusy(false);
    }
  };

  const pv = painVariant(risk?.pain_score);

  return (
    <IpdSectionCard
      title="Clinical risks"
      subtitle="Fall, pressure-ulcer and pain — the latest recorded values"
      actions={
        !disabled && !editing ? (
          <IpdButton variant="outlined" size="small" startIcon={<EditIcon />} onClick={openEditor}>
            {risk ? "Update" : "Record"}
          </IpdButton>
        ) : null
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {!risk && !editing ? (
        <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary }}>
          No risk assessment recorded yet.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Row label="Fall risk">
            <LevelChip level={risk?.fall_risk} />
          </Row>
          <Row label="Pressure-ulcer risk">
            <LevelChip level={risk?.pressure_ulcer_risk} />
          </Row>
          <Row label="Pain score">
            {risk?.pain_score == null ? (
              <Typography component="span" sx={{ fontSize: 13, color: IPD_COLORS.textMuted }}>
                Not recorded
              </Typography>
            ) : (
              <Box
                component="span"
                sx={{
                  ...ipdSemanticSx(pv),
                  px: 0.9,
                  py: 0.2,
                  borderRadius: `${IPD_SHAPE.radiusSm}px`,
                  fontSize: 12,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {risk.pain_score} / 10
              </Box>
            )}
          </Row>
          {risk?.updated_at && (
            <Typography sx={{ mt: 0.75, fontSize: 11.5, color: IPD_COLORS.textMuted }}>
              Updated {formatDateTime(risk.updated_at)}
              {risk.updated_by_name ? ` · ${risk.updated_by_name}` : ""}
            </Typography>
          )}
        </Box>
      )}

      <Collapse in={editing} unmountOnExit>
        <Box sx={{ mt: risk ? 2 : 0, display: "grid", gap: 1.5 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <Box>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Fall risk</Typography>
              <TextField
                select
                value={fallRisk}
                onChange={(e) => setFallRisk(e.target.value)}
                size="small"
                fullWidth
                sx={ipdElevatedInputSx}
                SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
              >
                {LEVEL_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Box>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Pressure-ulcer risk</Typography>
              <TextField
                select
                value={pressureUlcerRisk}
                onChange={(e) => setPressureUlcerRisk(e.target.value)}
                size="small"
                fullWidth
                sx={ipdElevatedInputSx}
                SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
              >
                {LEVEL_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
          <Box sx={{ maxWidth: 200 }}>
            <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Pain score (0–10)</Typography>
            <TextField
              value={painScore}
              onChange={(e) => setPainScore(e.target.value)}
              size="small"
              fullWidth
              type="number"
              sx={ipdElevatedInputSx}
              inputProps={{ min: 0, max: 10, "aria-label": "Pain score" }}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <IpdButton variant="outlined" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </IpdButton>
            <IpdButton onClick={save} loading={busy}>Save</IpdButton>
          </Box>
        </Box>
      </Collapse>
    </IpdSectionCard>
  );
}
